import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button.tsx'
import { Dialog, DialogContent } from '@/shad/ui/dialog.tsx'
import { MutationStatus } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckMarkIcon } from '../../icons/arrows'
import { CloseIcon, DocumentIcon, MagnifierIcon } from '../../icons/general'
import { RequestStatus } from '../../types_and_interfaces'
import { cn } from '../../utilities'
import AppAlertComponent from '../AppAlertComponent/AppAlertComponent'
import { Button } from '../Button'

interface Props {
  file: File | undefined
  onRetakeClick: () => void
  onContinueClick: (file: File) => void
  status: MutationStatus | RequestStatus
}

function DocumentPreviewNew(props: Props) {
  const { file, onRetakeClick, onContinueClick, status } = props
  const { t } = useTranslation()
  const [previewUrl, setPreviewUrl] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    if (!file) {
      return
    }

    const url = URL.createObjectURL(file)
    setPreviewUrl(url)

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [file])

  function isPDF() {
    return file && file.type === 'application/pdf'
  }

  const PreviewContent = () => (
    <div className="relative w-full h-auto overflow-auto">
      {isPDF() ? (
        <div className="min-h-full h-full text-center">
          <object
            data={previewUrl + '#toolbar=0'}
            type="application/pdf"
            className={cn(`w-full h-full min-h-[40dvh]`, {
              'min-h-[95dvh]': isModalOpen,
            })}
            title="PDF preview"
          >
            <DocumentIcon className="text-7xl text-primary-600 py-10" />
          </object>
        </div>
      ) : (
        <div
          className={cn('relative w-full h-full  overflow-auto', {
            'max-h-[95vh]': isModalOpen,
          })}
        >
          <img
            className="h-auto border-gray-200 transform-gpu transition-transform duration-200"
            src={previewUrl}
            alt="Document preview"
          />
        </div>
      )}
    </div>
  )

  if (!file) {
    return (
      <AppAlertComponent
        text="File not found"
        className="w-full"
        variant="info"
        title="File not found"
      >
        <div className="flex flex-col w-full">
          <div className="w-full">
            <Button className="w-full mt-4" onClick={onRetakeClick}>
              Try again
            </Button>
          </div>
        </div>
      </AppAlertComponent>
    )
  }

  return (
    <div className="flex justify-center flex-col ">
      <form
        onSubmit={(event) => {
          event.preventDefault()
          onContinueClick(file)
        }}
      >
        <div className="relative">
          <div className="border border-gray-200  overflow-hidden">
            <PreviewContent />
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="absolute top-4 right-6 h-10 w-10 rounded-lg bg-white/80 hover:bg-gray-100 shadow-sm"
            >
              <MagnifierIcon className="w-5 h-5 text-gray-600 bg" />
            </button>
          </div>

          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogContent className="max-w-7xl w-full h- p-0 [&>button]:hidden">
              <div className="relative w-full h-full">
                <div className="absolute h-8 w-8 top-6 right-10 z-50 flex items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="rounded-lg w-full h-full bg-white/80 hover:bg-gray-100 shadow-sm"
                  >
                    <CloseIcon className=" text-gray-700 m-0 p-0" />
                  </button>
                </div>
                <div className="w-full h-full p-4">
                  <PreviewContent />
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-10 flex items-center flex-col">
          <div className="text-emerald-600 flex items-center gap-2 justify-center text-center text-sm font-medium">
            <CheckMarkIcon className="text-xl" />
            Document Captured
          </div>
          <p className="text-gray-800 text-center">
            {isPDF() ? t("correct_pdf") : t("is_the_photo_clear")}
          </p>
          <div className="flex flex-col gap-2 w-full">
            <Button
              type="button"
              disabled={status === 'pending' || status === 'in_flight'}
              onClick={onRetakeClick}
              className="text-center mx-auto mt-4"
              variant="text"
            >
              {isPDF() ? t('select_different_file') : t('retake_photo')}
            </Button>
            <SubmitButton className="w-full flex-shrink-0" status={status} />
          </div>
        </div>
      </form>
    </div>
  )
}

export default DocumentPreviewNew
